import { fmtCompetencia, fmtCurrency } from './formatters';

/**
 * NOTAS DE EMPENHO COM SALDO (16/10/2026, fase 2 do plano de empenho, aprovada pelo Heitor: "aprovo o desenho"; "lista na
 * aba Contrato e reajuste, perto do campo de hoje"). Backend #468.
 *
 * O empenho é um registro da assinatura (número, emissão, valor, faixa de competências, escopo por produto, PDF). Cada
 * fatura aprovada consome dele; o SALDO é derivado no backend, em valor e em competências. Contrato sem nenhum empenho
 * cadastrado fica FORA do controle: nada aparece na aprovação. Cadastrar o primeiro empenho é o que liga.
 *
 * Os três campos de hoje CONTINUAM (o empenho do contrato, o do item e o da fatura): o nome do item na nota usa o do
 * item, senão o empenho cadastrado da competência, senão o do contrato. A tela não pode dar a entender que eles saíram.
 *
 * Decisões pelo CÓDIGO do aviso, nunca pelo texto. O "exige motivo" vem agregado do backend e é ele que manda.
 */
export type SaldoDoEmpenho = {
  valor: number; competenciasTotal: number; competenciasUsadas: number; competenciasRestantes: number; competenciasConsumidas?: string[];
};
export type ConsumoDoEmpenho = { invoiceId: number; competencia: string; value: number };
export type NotaDeEmpenho = {
  id: number; contractId: number; number: string; issuedAt: string; totalValue: string | number;
  competenciaInicio: string; competenciaFim: string; productIds: number[] | null;
  s3Key: string | null; fileName: string | null; notes: string | null;
  saldo?: SaldoDoEmpenho; consumos?: ConsumoDoEmpenho[];
};
export type CodigoDoAviso =
  | 'SEM_EMPENHO_PARA_COMPETENCIA' | 'ESGOTADO_EM_COMPETENCIAS' | 'SALDO_NAO_COBRE' | 'ITEM_SEM_EMPENHO' | 'FALTA_UMA_COMPETENCIA';
export type AvisoDeEmpenho = { codigo: CodigoDoAviso | string; mensagem: string; faltaValor?: number; empenhoId?: number };
export type SaldoNaPrevia = { valor: number; competencias: number };
export type BlocoDeEmpenho = {
  empenho: { id: number; number: string; competenciaInicio: string; competenciaFim: string };
  itens: Array<{ productId?: number | null; name?: string; valor?: number; [k: string]: any }>;
  valor: number; saldoAntes: SaldoNaPrevia; saldoDepois: SaldoNaPrevia; avisos: AvisoDeEmpenho[]; jaConsumiu?: boolean;
};
export type PreviaDeEmpenhos = {
  competencia: string | null; valorDaFatura: number; blocos: BlocoDeEmpenho[];
  itensSemEmpenho: Array<{ productId: number | null; name: string; valor: number; empenhoDeclarado?: string | null }>;
  avisos: AvisoDeEmpenho[]; exigeMotivo: boolean; foraDoControle: boolean;
};
export type PreviaDoRetroConsumo = {
  empenhoNumber: string; competenciaInicio: string; competenciaFim: string;
  entram: Array<{ invoiceId: number; code: string; competencia: string; valor: number }>;
  valorTotal: number; competenciasUsadas: string[]; saldoResultante: SaldoNaPrevia; avisos: AvisoDeEmpenho[];
};

/** Os que pedem motivo, para a tela marcar ("pede motivo"). Quem DECIDE é o `exigeMotivo` do backend. */
export const AVISOS_QUE_PEDEM_MOTIVO: ReadonlySet<string> = new Set(['SEM_EMPENHO_PARA_COMPETENCIA', 'ESGOTADO_EM_COMPETENCIAS', 'SALDO_NAO_COBRE', 'ITEM_SEM_EMPENHO']);
export const ROTULO_DO_AVISO: Record<string, string> = {
  SEM_EMPENHO_PARA_COMPETENCIA: 'Sem empenho para a competência',
  ESGOTADO_EM_COMPETENCIAS: 'Empenho esgotado em competências',
  SALDO_NAO_COBRE: 'Saldo não cobre esta fatura',
  ITEM_SEM_EMPENHO: 'Item sem empenho',
  FALTA_UMA_COMPETENCIA: 'Falta uma competência',
};

/** Dinheiro com o sinal na frente: "−R$ 1.028,43" (o saldo pode ficar negativo). */
export function fmtSaldo(valor: number | string | null | undefined): string {
  const n = Number(valor);
  if (!Number.isFinite(n)) return '—';
  return n < 0 ? `−${fmtCurrency(-n)}` : fmtCurrency(n);
}

/** "09/2026 a 12/2026" (ou só um mês). */
export function faixaDoEmpenho(e: { competenciaInicio: string; competenciaFim: string }): string {
  return e.competenciaInicio === e.competenciaFim
    ? fmtCompetencia(e.competenciaInicio)
    : `${fmtCompetencia(e.competenciaInicio)} a ${fmtCompetencia(e.competenciaFim)}`;
}

const comp = (n: number) => `${n} ${n === 1 ? 'competência' : 'competências'}`;
export const fraseDasCompetencias = comp;

/**
 * O EMPENHO ENCERRADO (E2, decisão do Heitor de 04/11/2026: "os empenhos que já foram consumidos não deveriam ficar
 * escondidos?"). Encerrado sai da lista dos ativos e vai para "Encerrados (N)", fechada por padrão:
 * - `esgotado`: nenhuma competência restante, ou saldo zero;
 * - `vencido`: a competência final é anterior ao mês corrente (a fatura do mês tem a competência do mês, então nenhuma
 *   fatura nova cai nele).
 * ⛔ Saldo NEGATIVO com competência restante fica nos ativos, mesmo vencido: é o "pedir reforço do empenho", e ainda
 * pede ação (confirmado pelo Heitor no mesmo dia). Null = ativo.
 */
export type MotivoDoEncerramento = 'esgotado' | 'vencido';
export function encerramentoDoEmpenho(
  e: Pick<NotaDeEmpenho, 'competenciaFim' | 'saldo'>, mesCorrente: string,
): MotivoDoEncerramento | null {
  const s = e.saldo;
  if (s && s.valor < -0.005 && s.competenciasRestantes > 0) return null;
  if (s && (s.competenciasRestantes <= 0 || Math.abs(s.valor) < 0.005)) return 'esgotado';
  if (e.competenciaFim && e.competenciaFim < mesCorrente) return 'vencido';
  return null;
}

/** "1 de 4 competências usadas · saldo R$ 21.975,00 · 3 competências". */
export function fraseDoSaldo(s: SaldoDoEmpenho | undefined): string {
  if (!s) return 'saldo não lido';
  return `${s.competenciasUsadas} de ${s.competenciasTotal} ${s.competenciasTotal === 1 ? 'competência usada' : 'competências usadas'} · saldo ${fmtSaldo(s.valor)} · ${comp(s.competenciasRestantes)}`;
}

/** "o contrato inteiro" ou a lista dos produtos cobertos. */
export function escopoDoEmpenho(productIds: number[] | null | undefined, produtos: Array<{ productId: number; nome: string }>): string {
  if (!productIds || productIds.length === 0) return 'o contrato inteiro';
  return productIds.map((id) => produtos.find((p) => p.productId === id)?.nome || `produto #${id}`).join(', ');
}

/** Valor digitado em reais ("29.300,00", "29300", "29300.5") → número. NaN quando não é valor. */
export function valorDigitado(v: string): number {
  const t = String(v || '').trim().replace(/^R\$\s*/, '');
  if (!t) return NaN;
  const normal = /,\d{1,2}$/.test(t) ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  return /^-?\d+(\.\d+)?$/.test(normal) ? Number(normal) : NaN;
}

export type FormularioDoEmpenho = {
  number: string; issuedAt: string; totalValue: string; competenciaInicio: string; competenciaFim: string;
  productIds: number[]; notes: string;
};
export const formularioVazio = (): FormularioDoEmpenho => ({
  number: '', issuedAt: '', totalValue: '', competenciaInicio: '', competenciaFim: '', productIds: [], notes: '',
});
export function formularioDe(e: NotaDeEmpenho): FormularioDoEmpenho {
  return {
    number: e.number || '', issuedAt: String(e.issuedAt || '').slice(0, 10),
    totalValue: Number(e.totalValue).toFixed(2).replace('.', ','),
    competenciaInicio: e.competenciaInicio || '', competenciaFim: e.competenciaFim || '',
    productIds: e.productIds || [], notes: e.notes || '',
  };
}

/** O que falta para gravar, nas palavras do backend. null = pode gravar. */
export function oQueFalta(f: FormularioDoEmpenho): string | null {
  if (!f.number.trim()) return 'o número da nota de empenho é obrigatório.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.issuedAt)) return 'a data de emissão é obrigatória.';
  const v = valorDigitado(f.totalValue);
  if (!Number.isFinite(v) || v <= 0) return 'o valor total do empenho tem de ser maior que zero.';
  const mes = /^\d{4}-(0[1-9]|1[0-2])$/;
  if (!mes.test(f.competenciaInicio) || !mes.test(f.competenciaFim)) return 'as competências inicial e final são obrigatórias.';
  if (f.competenciaInicio > f.competenciaFim) return `a competência inicial (${fmtCompetencia(f.competenciaInicio)}) é posterior à final (${fmtCompetencia(f.competenciaFim)}).`;
  return null;
}

/**
 * O corpo que vai ao backend. Escopo vazio vai como `[]` e observação vazia como `''`: o PUT mescla com `??`, e `null`
 * manteria o valor antigo (não daria para limpar).
 */
export function corpoDoEmpenho(f: FormularioDoEmpenho) {
  return {
    number: f.number.trim(), issuedAt: f.issuedAt, totalValue: valorDigitado(f.totalValue),
    competenciaInicio: f.competenciaInicio, competenciaFim: f.competenciaFim,
    productIds: [...f.productIds], notes: f.notes.trim(),
  };
}

/** Só PDF sobe: o backend aceita qualquer arquivo, a tela não (combinado em 16/10/2026). */
export function ehPdf(file: File | null | undefined): boolean {
  if (!file) return false;
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** Os produtos da assinatura (os itens com produto), sem repetir, para a escolha do escopo. */
export function produtosDaAssinatura(itens: any[] | null | undefined): Array<{ productId: number; nome: string }> {
  const vistos = new Map<number, string>();
  for (const it of itens || []) {
    const id = Number(it?.productId);
    if (Number.isFinite(id) && id > 0 && !vistos.has(id)) vistos.set(id, String(it.name || it.productCode || `produto #${id}`));
  }
  return Array.from(vistos, ([productId, nome]) => ({ productId, nome }));
}

/** Os avisos que não são de um bloco (sem empenho na competência, item sem empenho), separados dos de cada bloco. */
export function avisosForaDosBlocos(p: PreviaDeEmpenhos): AvisoDeEmpenho[] {
  return (p.avisos || []).filter((a) => !a.empenhoId);
}

/** O ACEITE gravado na fatura (`invoices.empenhoAviso`, backend #468): quem aprovou com aviso, quando e por quê. */
export type AceiteDoAviso = { codigos: string[]; nota: string; userId: number | null; at: string | null };

/** Lê o `empenhoAviso` da fatura. Sem motivo escrito não há aceite a mostrar (o backend só grava com motivo). */
export function aceiteDoAviso(inv: any): AceiteDoAviso | null {
  const a = inv?.empenhoAviso;
  if (!a || typeof a !== 'object') return null;
  const nota = typeof a.nota === 'string' ? a.nota.trim() : '';
  if (!nota) return null;
  const userId = Number(a.userId);
  return {
    codigos: Array.isArray(a.codigos) ? a.codigos.filter((c: unknown) => typeof c === 'string') : [],
    nota,
    userId: Number.isFinite(userId) && userId > 0 ? userId : null,
    at: typeof a.at === 'string' && a.at ? a.at : null,
  };
}

/** Os avisos aceitos, pelo rótulo da tela; código que a tela não conhece aparece como veio. */
export function avisosDoAceite(a: AceiteDoAviso): string {
  return a.codigos.map((c) => ROTULO_DO_AVISO[c] || c).join(', ');
}

/**
 * Quem aprovou. A fatura guarda só o `userId` (sai do token no backend). "você" quando é quem está logado; o nome quando
 * a tela conseguiu ler (`GET /users/:id` é de admin); senão "usuário #id". Nunca um nome adivinhado.
 */
export function autorDoAceite(a: AceiteDoAviso, eu: { id?: number; name?: string } | null, nomeLido?: string | null): string {
  if (a.userId == null) return 'autor não registrado';
  if (eu?.id === a.userId) return eu.name ? `${eu.name} (você)` : 'você';
  if (nomeLido) return nomeLido;
  return `usuário #${a.userId}`;
}
